/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { ALL_PERMISSIONS } from "../../hooks/useTenantStorePermissions";

export const ONBOARDING_DRAFT_KEY = "onboardingCompanyDraft";
export const ONBOARDING_STEP_KEY = "onboardingStep";

const LOGO_MAX_DATA_URL = 1_800_000;

export function parseStorageJSON(raw, fallback = {}) {
  try {
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function setOnboardingStep(step) {
  if (step) localStorage.setItem(ONBOARDING_STEP_KEY, step);
}

export function getOnboardingStep() {
  return localStorage.getItem(ONBOARDING_STEP_KEY) || "";
}

export function loadCompanyInfoDraft() {
  return parseStorageJSON(localStorage.getItem(ONBOARDING_DRAFT_KEY), null);
}

export function saveCompanyInfoDraft(draft) {
  try {
    const payload = {
      storeName: draft.storeName || "",
      storeSlug: draft.storeSlug || "",
      sector: draft.sector || "",
      paymentMethods: Array.isArray(draft.paymentMethods) ? draft.paymentMethods : [],
      services: draft.services || "",
      logoDataUrl: draft.logoDataUrl || "",
      preview: draft.preview || draft.logoDataUrl || "",
      savedAt: Date.now(),
    };
    if (payload.logoDataUrl && payload.logoDataUrl.length > LOGO_MAX_DATA_URL) {
      payload.logoDataUrl = "";
      if (String(payload.preview).startsWith("data:") && payload.preview.length > LOGO_MAX_DATA_URL) {
        payload.preview = "";
      }
    }
    localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn("Could not persist onboarding draft:", err);
  }
}

export function clearCompanyInfoDraft() {
  localStorage.removeItem(ONBOARDING_DRAFT_KEY);
}

export const SIGNUP_DRAFT_KEY = "onboardingSignupDraft";

export function saveSignupDraft(draft) {
  try {
    localStorage.setItem(
      SIGNUP_DRAFT_KEY,
      JSON.stringify({
        fullName: draft.fullName || "",
        engFullName: draft.engFullName || draft.fullName || "",
        phone: draft.phone || "",
        email: draft.email || "",
        storeName: draft.storeName || "",
        storeSlug: draft.storeSlug || "",
        savedAt: Date.now(),
      })
    );
  } catch {
    /* ignore quota */
  }
}

export function loadSignupDraft() {
  return parseStorageJSON(localStorage.getItem(SIGNUP_DRAFT_KEY), null);
}

export function clearSignupDraft() {
  localStorage.removeItem(SIGNUP_DRAFT_KEY);
}

export function clearOnboardingSession() {
  clearCompanyInfoDraft();
  clearSignupDraft();
  localStorage.removeItem(ONBOARDING_STEP_KEY);
}

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function dataUrlToFile(dataUrl, filename = "store-logo.webp") {
  if (!dataUrl || !String(dataUrl).startsWith("data:")) return null;
  try {
    const [header, base64] = String(dataUrl).split(",");
    const mime = (header.match(/data:(.*?);/) || [])[1] || "image/png";
    const binary = atob(base64 || "");
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch {
    return null;
  }
}

export function isAccountExistsError(message) {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("already registered") ||
    m.includes("already exist") ||
    m.includes("email already") ||
    m.includes("already in use") ||
    m.includes("duplicate") ||
    m.includes("مسجل بالفعل") ||
    m.includes("مسجل مسبقا") ||
    m.includes("الحساب موجود") ||
    m.includes("موجود مسبقا") ||
    m.includes("البريد مستخدم")
  );
}

function pickErrorText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map(pickErrorText).filter(Boolean).join(" ");
  if (typeof value === "object") {
    return pickErrorText(value.message || value.Message || value.error || value.Error);
  }
  return String(value).trim();
}

export function getRegisterErrorMessage(data, fallback = "") {
  if (typeof data === "string") return data.trim() || fallback;
  if (!data || typeof data !== "object") return fallback;

  const errors = data.errors || data.Errors || {};
  const slugMsg =
    pickErrorText(errors.storeSlug) ||
    pickErrorText(errors.StoreSlug) ||
    pickErrorText(data.storeSlug);

  return (
    slugMsg ||
    pickErrorText(data.message) ||
    pickErrorText(data.Message) ||
    pickErrorText(data.error) ||
    pickErrorText(data.Error) ||
    pickErrorText(data.title) ||
    pickErrorText(data.Title) ||
    fallback
  );
}

export function isStoreSlugTakenError(message, data) {
  const errors = data && typeof data === "object" ? data.errors || data.Errors || {} : {};
  if (errors.storeSlug || errors.StoreSlug) return true;

  const blob = [
    message,
    typeof data === "string" ? data : "",
    data?.message,
    data?.Message,
    JSON.stringify(errors),
  ]
    .join(" ")
    .toLowerCase();

  return (
    blob.includes("storeslug") ||
    blob.includes("store slug") ||
    blob.includes("store-slug") ||
    blob.includes("معرف المتجر") ||
    blob.includes("يوزر المتجر") ||
    (blob.includes("slug") &&
      (blob.includes("taken") ||
        blob.includes("already") ||
        blob.includes("exist") ||
        blob.includes("مستخدم") ||
        blob.includes("مأخوذ")))
  );
}

function parseJsonClaim(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function parseJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    try {
      return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
      return null;
    }
  }
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
}

export function getTenantUsersFromAuthData(data) {
  if (Array.isArray(data?.users) && data.users.length) return data.users;
  if (Array.isArray(data?.tenantUsersList) && data.tenantUsersList.length) {
    return data.tenantUsersList;
  }
  if (data?.tenantUser) return asArray(data.tenantUser);

  const claims = parseJwtPayload(data?.token);
  if (claims) {
    const fromUsers = parseJsonClaim(claims.TenantUsers);
    if (asArray(fromUsers).length) return asArray(fromUsers);
    const fromUser = parseJsonClaim(claims.TenantUser);
    if (asArray(fromUser).length) return asArray(fromUser);
  }

  return [];
}

function userEmail(user) {
  return String(user?.email || user?.Email || "").toLowerCase().trim();
}

function pickUser(data, identifier) {
  const users = getTenantUsersFromAuthData(data);
  const loginId = String(identifier || "").toLowerCase().trim();
  if (loginId) {
    const matched = users.find((u) => userEmail(u) === loginId);
    if (matched) return matched;
  }
  return users[0] || data?.tenantUser || data?.tenant || null;
}

function normalizePermissions(apiUser) {
  const map = {};
  Object.keys(apiUser || {}).forEach((key) => {
    if (typeof apiUser[key] === "number") {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      map[camelKey] = apiUser[key];
    }
  });
  return map;
}

export function isAuthSubscriptionExpired(data) {
  if (data == null) {
    if (localStorage.getItem("subscriptionExpired") === "true") return true;
    const stored = parseStorageJSON(localStorage.getItem("subscription"), null);
    return stored?.isExpired === true || stored?.isExpired === 1;
  }

  const flag = data.subscriptionExpired;
  if (flag === true || flag === 1 || flag === "true") return true;
  if (flag === false || flag === 0 || flag === "false") return false;

  const expired = data?.subscription?.isExpired;
  if (expired === true || expired === 1 || expired === "true") return true;
  if (expired === false || expired === 0 || expired === "false") return false;

  return false;
}

export function persistSubscriptionState(data) {
  const subscription =
    data?.subscription && typeof data.subscription === "object" ? data.subscription : null;
  const expired = isAuthSubscriptionExpired(data);

  localStorage.setItem("subscriptionExpired", expired ? "true" : "false");

  if (subscription) {
    localStorage.setItem("subscription", JSON.stringify(subscription));
    if (subscription.expirationDate) {
      localStorage.setItem("plan_expiry_date", String(subscription.expirationDate));
    }
    if (subscription.isTrial === true || subscription.isTrial === 1) {
      localStorage.setItem("isTrial", "true");
    } else if (subscription.isTrial === false || subscription.isTrial === 0) {
      localStorage.setItem("isTrial", "false");
    }
  }

  return {
    subscription,
    expired,
    notification: subscription?.notification || "",
  };
}

export function getAuthRedirectPath(data, { authRole } = {}) {
  if (authRole === "superAdmin") return "/admin";
  if (isAuthSubscriptionExpired(data)) return "/theme-plan";
  return "/admin-store";
}

export function persistTenantAuth(data, authLogin, { source, identifier } = {}) {
  const token = data?.token || "";
  if (!token) return false;

  const claims = parseJwtPayload(token) || {};
  const jwtDetails = parseJsonClaim(claims.TenantsDtl) || {};
  const jwtPlans = parseJsonClaim(claims.Plans);
  const jwtThemes = parseJsonClaim(claims.Themes);
  const jwtPayments = parseJsonClaim(claims.TenantPaymentMethods);

  const tenant = data?.tenant || {};
  const tenantDetails =
    data?.tenantDetails && typeof data.tenantDetails === "object" && Object.keys(data.tenantDetails).length
      ? data.tenantDetails
      : jwtDetails;
  const users = getTenantUsersFromAuthData(data);
  const user = pickUser(data, identifier);
  const plans = Array.isArray(data?.plans) ? data.plans : asArray(jwtPlans);
  const themes = Array.isArray(data?.themes) ? data.themes : asArray(jwtThemes);
  const paymentMethods = Array.isArray(data?.paymentMethods)
    ? data.paymentMethods
    : asArray(jwtPayments);

  const tenantId =
    tenant?.tenantId ||
    tenant?.TenantId ||
    tenantDetails?.tenantId ||
    tenantDetails?.TenantId ||
    user?.tenantId ||
    user?.TenantId ||
    claims.TenantId ||
    "";

  const userFunctionId =
    user?.userFunctionId ??
    user?.userFunctionID ??
    user?.UserFunctionId ??
    user?.UserFunctionID;

  const emailMatches =
    tenant?.email &&
    userEmail(user) &&
    String(tenant.email).toLowerCase().trim() === userEmail(user);

  const isTenantOwner =
    source === "register" ||
    userFunctionId === null ||
    userFunctionId === undefined ||
    Number(userFunctionId) === 0 ||
    emailMatches;

  const { subscription, expired, notification } = persistSubscriptionState(data);

  localStorage.setItem("token", token);
  localStorage.setItem("authToken", token);
  localStorage.setItem("loginMessage", data?.message || "");
  localStorage.setItem("tenant", JSON.stringify(tenant || {}));
  localStorage.setItem("tenantId", tenantId ? String(tenantId) : "");
  localStorage.setItem(
    "tenantEmail",
    tenant.email || tenant.Email || user?.email || user?.Email || ""
  );
  localStorage.setItem(
    "tenantPhone",
    tenant.phone || tenant.Phone || user?.phone || user?.Phone || ""
  );
  localStorage.setItem("tenantUsersList", JSON.stringify(users.length ? users : user ? [user] : []));
  localStorage.setItem("tenantDB", tenant.databaseName || tenant.DatabaseName || "");
  localStorage.setItem("tenantDetails", JSON.stringify(tenantDetails || {}));
  localStorage.setItem("plans", JSON.stringify(plans));
  localStorage.setItem("themes", JSON.stringify(themes));
  localStorage.setItem("paymentMethods", JSON.stringify(paymentMethods));

  if (source === "register") {
    localStorage.setItem("fullRegisterResponse", JSON.stringify(data));
  } else if (source === "login") {
    localStorage.setItem("fullLoginResponse", JSON.stringify(data));
  }

  const slug =
    tenant.storeSlug ||
    tenant.StoreSlug ||
    tenantDetails.storeSlug ||
    tenantDetails.StoreSlug ||
    "";
  if (slug) localStorage.setItem("companyStoreSlug", slug);

  const storeName = tenantDetails.clientName || tenantDetails.ClientName || "";
  if (storeName) localStorage.setItem("companyStoreName", storeName);

  const authUser =
    user && typeof user === "object"
      ? user
      : { email: tenant.email || tenant.Email, tenantId };

  if (typeof authLogin === "function") {
    authLogin({
      token,
      user: authUser,
      authRole: "tenant",
      isTenantOwner,
      permissions: isTenantOwner ? ALL_PERMISSIONS : normalizePermissions(user),
    });
  }

  return {
    user: authUser,
    tenant,
    tenantDetails,
    isTenantOwner,
    subscription,
    subscriptionExpired: expired,
    notification,
    nextPath: getAuthRedirectPath(data),
  };
}

export function recoverTenantAuth(authLogin) {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token") || "";
  if (!token) return false;

  if (!localStorage.getItem("authToken") && token) {
    localStorage.setItem("authToken", token);
  }

  let user = parseStorageJSON(localStorage.getItem("user"), null);
  if (!user || typeof user !== "object") {
    const registerData = parseStorageJSON(localStorage.getItem("fullRegisterResponse"), {});
    const loginData = parseStorageJSON(localStorage.getItem("fullLoginResponse"), {});
    user =
      pickUser(registerData) ||
      pickUser(loginData) ||
      parseStorageJSON(localStorage.getItem("tenant"), null);
  }

  if (!user || typeof user !== "object") return Boolean(token);

  if (typeof authLogin === "function") {
    const role = localStorage.getItem("authRole") || "tenant";
    const isTenantOwner = localStorage.getItem("isTenantOwner") !== "false";
    authLogin({
      token,
      user,
      authRole: role,
      isTenantOwner,
      permissions:
        parseStorageJSON(localStorage.getItem("permissions"), null) || ALL_PERMISSIONS,
    });
  }

  return true;
}

export function getOnboardingResumePath() {
  return isAuthSubscriptionExpired(null) ? "/theme-plan" : "/admin-store";
}

export function hasOnboardingSession() {
  return Boolean(localStorage.getItem("authToken") || localStorage.getItem("token"));
}

export async function loginExistingTenant(email, password, baseUrl) {
  const res = await fetch(`${baseUrl}/Login/UserLogin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email: String(email || "").trim(),
      password,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.Message || `HTTP ${res.status}`);
  }
  return data;
}
